import * as R from 'ramda';
import {connect} from 'react-redux';
import {Modal} from 'react-bootstrap';
import React from 'react';
import Bluebird from 'bluebird';
import {FeatureFlag} from 'react-launch-darkly';
import moment from 'moment/moment';
import classNames from 'classnames';

import ContentRow from 'js/components/content_row';
import Collapsable from 'js/components/collapsable';
import SubmittedContentLabels from 'js/components/submitted_content_labels';
import LoadingSpinner from 'js/components/loading_spinner';
import OptionsDropdown from 'js/components/options_dropdown';
import ContentReviewWorkflow from 'js/components/content_review_workflow';
import CampaignContentProgress from 'js/components/campaign_content_progress';
import ApprovalAndListViewToggle from 'js/components/approval_and_list_view_toggle';
import NoSubmittedContent from 'js/components/no_submitted_content';
import {queryCampaignType} from 'js/graphql/queries/queryCampaignType';

import * as CampaignDucks from './ducks/campaigns';
import {mutationEditRequestCreate} from '../../graphql/mutations/mutationEditRequestCreate';
import {getAuth0Storage} from '../../auth0_storage';
import {queryEditRequest} from '../../graphql/queries/queryEditRequest';
import {sendBrandContentFeedbackEmail} from '../brand_api_client';
import ExtendedApprovalFlow from '../../approval_queues/components/ExtendedApprovalFlow';
import CreatorFilter from './creator_filter/creator_filter';

import 'scss/content_list.scss';

const filters = {
    ALL: 'all',
    PENDING: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected'
};

const sorts = {
    DESC: 'desc',
    ASC: 'asc',
    USERNAME: 'username'
};

const contentFilters = [
    {id: filters.ALL, label: 'All Statuses'},
    {id: filters.PENDING, label: 'Pending'},
    {id: filters.APPROVED, label: 'Approved'},
    {id: filters.REJECTED, label: 'Rejected'}
];

const contentSorts = [
    {id: sorts.DESC, label: 'Newest To Oldest'},
    {id: sorts.ASC, label: 'Oldest To Newest'},
    {id: sorts.USERNAME, label: 'Creator Handle'}
];

class ContentList extends ExtendedApprovalFlow {
    static defaultProps = {
        campaign: {},
        campaign_contents: null,
        campaign_content_progress: {}
    };

    constructor(props) {
        super(props);
        this.state = {
            collapsed: [],
            content: null,
            filter: filters.ALL,
            sort: sorts.DESC,
            contents: props.campaign_contents,
            selectedCreatorIds: [],
            allCreators: [],
            isLoadingCreators: false
        };
    }

    async UNSAFE_componentWillReceiveProps(nextProps) {
        const {contents} = this.state;
        if (R.isNil(contents) || R.isEmpty(contents)) {
            //this.setState({contents: nextProps.campaign_contents});
        }
    }

    componentDidMount() {
        this.fetchCampaign();
    }

    fetchCampaign = async () => {
        const {
            fetchCampaign,
            fetchCampaignContents,
            params: {campaignSlug},
            fetchCampaignGuidelines,
            fetchCampaignContentProgress
        } = this.props;
        const {filter, sort, selectedCreatorIds} = this.state;

        await this.setStateAsync({
            isLoading: true,
            isLoadingCreators: true,
            contents: []
        });

        await Bluebird.all([
            fetchCampaign(campaignSlug),
            fetchCampaignGuidelines(campaignSlug),
            fetchCampaignContentProgress(campaignSlug),
            fetchCampaignContents(campaignSlug, {
                filter,
                sort,
                ...selectedCreatorIds.reduce((acc, i, idx) => {
                    acc[`handle[${idx}]`] = i;
                    return acc;
                }, {})
            })
        ]);

        const pendingContentIds = this.props.campaign_contents
            .filter(content => content.status === 'pending')
            .map(p => p.id);

        const contentEditRequests = await this.getEditRequest(pendingContentIds);
        const allCreators = this.extractCreatorsFromCampaignConfig(this.props.campaign);

        await this.setStateAsync({
            isLoading: false,
            isLoadingCreators: false,
            contents: this.props.campaign_contents.map(content => {
                content.editRequest = contentEditRequests?.editRequest.find(
                    r => Number(r?.contentId) === content.id
                );
                content.internalMessages = contentEditRequests?.internalMessages.filter(
                    iM => Number(iM?.contentId) === content.id
                );
                return content;
            }),
            allCreators
        });
        
        if (this.props.campaign && this.props.campaign.campaignId) {
            this.getCampaignType();
        }
    };

    extractCreatorsFromCampaignConfig = config => {
        return (config?.creators ?? []).map(creator => ({
            id: creator,
            name: creator
        }));
    };

    // Handle creator selection
    handleCreatorSelect = creatorId => {
        this.setState(prevState => {
            const {selectedCreatorIds} = prevState;
            const isSelected = selectedCreatorIds.includes(creatorId);

            // Toggle selection
            const newSelectedCreatorIds = isSelected
                ? selectedCreatorIds.filter(id => id !== creatorId)
                : [...selectedCreatorIds, creatorId];

            return {selectedCreatorIds: newSelectedCreatorIds};
        }, this.applyFilters);
    };

    // Clear all creator filters
    clearCreatorFilters = () => {
        this.setState({selectedCreatorIds: []}, this.applyFilters);
    };

    // Apply all filters (creator filter + existing filters)
    applyFilters = async () => {
        const {filter, sort, selectedCreatorIds} = this.state;
        const {
            fetchCampaignContents,
            params: {campaignSlug}
        } = this.props;

        await this.setStateAsync({isLoading: true});

        // Fetch filtered content
        await fetchCampaignContents(campaignSlug, {
            filter,
            sort,
            ...selectedCreatorIds.reduce((acc, i, idx) => {
                acc[`handle[${idx}]`] = i;
                return acc;
            }, {})
        });

        await this.setStateAsync({
            isLoading: false,
            contents: this.props.campaign_contents
        });
    };

    setStateAsync(state) {
        return new Promise(resolve => {
            this.setState(state, resolve);
        });
    }

    gotoApprovalView = async contentId => {
        const {
            params: {campaignSlug},
            fetchOldestCampaignContent,
            fetchCampaignContent
        } = this.props;

        await this.setStateAsync({isLoading: true});
        if (R.is(Number)(contentId)) {
            await fetchCampaignContent(campaignSlug, contentId);
        } else {
            await fetchOldestCampaignContent(campaignSlug);
        }
        await this.setStateAsync({isLoading: false});
    };

    reviewContentById = async id => {
        await this.setStateAsync({isLoading: true});
        this.props.fetchCampaignContent(this.props.params.campaignSlug, id);
    };

    collapseRowIfNecessary = id => {
        const {filter} = this.state;
        if (filter === filters.PENDING) {
            this.setState(
                R.evolve({
                    collapsed: R.append(id)
                })
            );
        }
    };

    setContentStatus = (status, contentId) =>
        R.map(R.when(R.propEq('id', contentId), R.assoc('status', status)));

    setContentEditRequest = (editRequest, contentId) =>
        R.map(R.when(R.propEq('id', contentId), R.assoc('editRequest', editRequest)));

    onFilter = async filter => {
        const {
            fetchCampaignContents,
            params: {campaignSlug}
        } = this.props;
        await this.setStateAsync({filter, isLoading: true});

        await fetchCampaignContents(campaignSlug, {filter, sort: this.state.sort});

        await this.setStateAsync({
            isLoading: false,
            collapsed: [],
            content: null,
            contents: this.props.campaign_contents
        });
    };

    onSort = async sort => {
        const {
            fetchCampaignContents,
            params: {campaignSlug}
        } = this.props;

        await this.setStateAsync({sort, isLoading: true});
        await fetchCampaignContents(campaignSlug, {sort, filter: this.state.filter});

        await this.setStateAsync({
            isLoading: false,
            collapsed: [],
            content: null,
            contents: this.props.campaign_contents
        });
    };

    onApprove = async (contentMetadataId, data, tiktokCsfEnabled) => {
        await this.props.approveContent(contentMetadataId, data);

        const content = this.props.campaign_contents.find(c => c.id === contentMetadataId) || {};

        if (tiktokCsfEnabled && this.isContentVideo(content.content_type)) {
            await this.handleUploadToTiktokCSF(contentMetadataId);
        } else {
            await this.handleLinkContentToCampaign(contentMetadataId);
        }

        this.collapseRowIfNecessary(contentMetadataId);
        this.setState(
            R.evolve({
                contents: this.setContentStatus('approved', contentMetadataId)
            })
        );
    };

    handleLinkContentToCampaign = async contentId => {
        try {
            const {campaign_contents, campaign} = this.props;
            const content = campaign_contents.find(c => c.id === contentId);

            if (!content) {
                console.error('Cannot link content to campaign. Content not found');
                return;
            }

            const linkContentToCampaignPayload = {
                sn_media_id: content.media_id,
                sn_brand_id: campaign.brandId,
                sn_campaign_id: campaign.campaignId,
                sn_content_type: content.content_type.toUpperCase()
            };

            const linkContentResult = await this.linkContentToCampaign(
                linkContentToCampaignPayload,
                campaign.campaignContentWorkflow
            );

            if (linkContentResult && !linkContentResult.wasSuccessful && linkContentResult.error) {
                console.error(linkContentResult.error);
            }

            return linkContentResult;
        } catch (error) {
            console.error('Error linking content to campaign', error);
        }

        return Promise.resolve();
    };

    handleUploadToTiktokCSF = async contentId => {
        try {
            const {campaign_contents, campaign} = this.props;
            const content = campaign_contents.find(c => c.id === contentId);

            if (!content) {
                console.error('Cannot upload to TikTok CSF. Content not found');
                return;
            }

            const tiktokCSFPayload = {
                sn_media_id: content.media_id,
                sn_campaign_name: campaign.campaignName,
                sn_brand_id: campaign.brandId,
                sn_campaign_id: campaign.campaignId
            };

            const uploadToTikTokResult = await this.uploadContentToTikTokCSF(
                tiktokCSFPayload,
                content.content_type,
                campaign.campaignContentWorkflow
            );

            if (
                uploadToTikTokResult &&
                !uploadToTikTokResult.wasSuccessful &&
                uploadToTikTokResult.error
            ) {
                console.error(uploadToTikTokResult.error);
            }

            return uploadToTikTokResult;
        } catch (error) {
            console.error('Error uploading content to TikTok CSF', error);
        }

        return Promise.resolve();
    };

    onReject = async (contentMetadataId, guidelines, {selectedOptions, feedbackText}) => {
        await this.props.rejectContent(contentMetadataId, {
            missedGuidelines: R.map(R.prop('guideline'), guidelines),
            reasons: selectedOptions,
            feedback: feedbackText
        });
        this.collapseRowIfNecessary(contentMetadataId);
        this.setState(
            R.evolve({
                content: R.always(null),
                contents: this.setContentStatus('rejected', this.state.content.id)
            })
        );
    };

    onDoNotDistribute = async (
        contentMetadataId,
        {selectedOptions, feedbackText},
        tiktokCsfEnabled
    ) => {
        await this.props.doNotDistributeContent(contentMetadataId, {
            reasons: selectedOptions,
            feedback: feedbackText
        });

        const content = this.props.campaign_contents.find(c => c.id === contentMetadataId) || {};

        if (tiktokCsfEnabled && this.isContentVideo(content.content_type)) {
            await this.handleUploadToTiktokCSF(contentMetadataId);
        } else {
            await this.handleLinkContentToCampaign(contentMetadataId);
        }

        this.collapseRowIfNecessary(contentMetadataId);
        this.setState(
            R.evolve({
                content: R.always(null),
                contents: this.setContentStatus('rejected', contentMetadataId)
            })
        );
    };

    onRequestSubmit = async (contentMetadataId, message, emails) => {
        const result = await mutationEditRequestCreate({
            contentId: contentMetadataId,
            message: message
        });
        if (result?.data?.editRequestCreate?.wasSuccessful === true) {
            const {auth0Profile} = getAuth0Storage();
            const newEditRequest = {
                message: message,
                createdAt: moment().format(),
                emailAddress: auth0Profile.email
            };
            this.setState(
                R.evolve({
                    content: R.always(null),
                    contents: this.setContentEditRequest(newEditRequest, contentMetadataId)
                })
            );
            await sendBrandContentFeedbackEmail({
                contentId: contentMetadataId,
                campaignSlug: this.props.params.campaignSlug,
                emails: emails,
                editRequest: message
            });
        }
    };

    getEditRequest = contentId => {
        return queryEditRequest(contentId)
            .then(res => {
                const {auth0Profile} = getAuth0Storage();
                return {
                    editRequest: res?.editRequests?.filter(
                        r => r.emailAddress === auth0Profile.email
                    ),
                    internalMessages: res?.internalMessages
                };
            })
            .catch(error => console.error('Request edit get error: ', error));
    };

    onModalClose = () => {
        this.setState({content: null});
    };

    getCampaignType() {
        const campaignId = this.props.campaign.campaignId;

        if (!campaignId) {
            console.error('No campaignId found in props for getCampaignType');
            return;
        }

        return queryCampaignType(campaignId)
            .then(campaignType => {
                this.setState({campaignType});
            })
            .catch(error => {
                console.error(error);
                throw new Error(error);
            });
    }

    renderRow = content => {
        const {isOps} = window.sn;
        const {
            id,
            status,
            caption,
            platform,
            comments,
            social_id,
            content_url,
            content_type,
            profile_image_url,
            brand_approval_due_date,
            post_state,
            editRequest,
            internalMessages,
            content_sn_type,
            content_sn_type_id,
            created_at,
            tags,
            admin_status
        } = content;

        const {isEditsAllowed, brandEmails} = this.props.campaign;
        const {collapsed, campaignType} = this.state;

        // Transform `platform` if `campaignType` is equal to `tiktok`
        const transformPlatform = campaignType === 'tiktok' ? 'tiktok' : platform;

        return (
            <div key={id} className="content-list__row">
                <Collapsable collapsed={R.contains(id, collapsed)}>
                    <FeatureFlag
                        flagKey="upload-to-tiktok-csf-enabled"
                        renderFeatureCallback={featureFlagValue => {
                            return (
                                <ContentRow
                                    postState={post_state}
                                    adminStatus={admin_status || null}
                                    status={status}
                                    caption={caption}
                                    platform={transformPlatform}
                                    comments={comments}
                                    showComments={isOps}
                                    socialId={social_id}
                                    contentUrl={content_url}
                                    contentType={content_type}
                                    profileImageUrl={profile_image_url}
                                    gotoApprovalView={() => this.gotoApprovalView(id)}
                                    onReject={() => this.setState({content})}
                                    onApprove={data =>
                                        this.onApprove(
                                            id,
                                            data,
                                            featureFlagValue === 'on' || featureFlagValue === true
                                        )
                                    }
                                    brandApprovalDueDate={brand_approval_due_date}
                                    onReview={() => this.reviewContentById(id)}
                                    onDoNotDistribute={feedback =>
                                        this.onDoNotDistribute(
                                            id,
                                            {
                                                selectedOptions: ['not_brand_fit'],
                                                feedbackText:
                                                    'The brand did not want to distribute this post.'
                                            },
                                            featureFlagValue === 'on' || featureFlagValue === true
                                        )
                                    }
                                    onRequestSubmit={(message, emails) =>
                                        this.onRequestSubmit(id, message, emails)
                                    }
                                    editRequest={editRequest}
                                    internalMessages={internalMessages}
                                    isEditsAllowed={isEditsAllowed}
                                    brandEmails={brandEmails}
                                    contentSnType={content_sn_type}
                                    contentSnTypeId={content_sn_type_id}
                                    contentCreatedAt={created_at}
                                    tags={tags}
                                    contentMetadataId={id} // Pass the content ID as contentMetadataId
                                />
                            );
                        }}
                        renderDefaultCallback={() => (
                            <ContentRow
                                postState={post_state}
                                adminStatus={admin_status || null}
                                status={status}
                                caption={caption}
                                platform={transformPlatform}
                                comments={comments}
                                showComments={isOps}
                                socialId={social_id}
                                contentUrl={content_url}
                                contentType={content_type}
                                profileImageUrl={profile_image_url}
                                gotoApprovalView={() => this.gotoApprovalView(id)}
                                onReject={() => this.setState({content})}
                                onApprove={data => this.onApprove(id, data, false)}
                                brandApprovalDueDate={brand_approval_due_date}
                                onReview={() => this.reviewContentById(id)}
                                onDoNotDistribute={feedback =>
                                    this.onDoNotDistribute(
                                        id,
                                        {
                                            selectedOptions: ['not_brand_fit'],
                                            feedbackText:
                                                'The brand did not want to distribute this post.'
                                        },
                                        false
                                    )
                                }
                                onRequestSubmit={(message, emails) =>
                                    this.onRequestSubmit(id, message, emails)
                                }
                                editRequest={editRequest}
                                internalMessages={internalMessages}
                                isEditsAllowed={isEditsAllowed}
                                brandEmails={brandEmails}
                                contentSnType={content_sn_type}
                                contentSnTypeId={content_sn_type_id}
                                contentCreatedAt={created_at}
                                tags={tags}
                                contentMetadataId={id} // Pass the content ID as contentMetadataId
                            />
                        )}
                    />
                </Collapsable>
            </div>
        );
    };

    renderList = () => {
        const {contents} = this.state;

        if (R.isNil(contents)) {
            return (
                <div className="content-list__loading layout-middle layout-center">
                    <LoadingSpinner additionalClasses="fa-3x" />
                </div>
            );
        }

        // todo: remove when backend for creator filter is ready
        const filteredContents = this.state.selectedCreatorIds.length
            ? R.filter(
                  content => this.state.selectedCreatorIds.includes(content.social_id),
                  contents
              )
            : contents;

        if (!filteredContents.length) {
            return <NoSubmittedContent />;
        }

        return <div className="content-list__list">{R.map(this.renderRow, filteredContents)}</div>;
    };

    renderModal = () => {
        const {campaign_guidelines: guidelines} = this.props;
        const {content} = this.state;
        return (
            <Modal
                show={!R.isNil(content)}
                className="content-list__modal"
                onHide={this.onModalClose}
            >
                <div className="content-list__modal__contents">
                    <i
                        className="fa fa-close content-list__modal__close"
                        onClick={this.onModalClose}
                    />
                    <FeatureFlag
                        flagKey="upload-to-tiktok-csf-enabled"
                        renderFeatureCallback={featureFlagValue => (
                            <ContentReviewWorkflow
                                rejecting
                                isModal
                                guidelines={guidelines}
                                contentId={R.propOr(null, 'id', content)}
                                caption={R.propOr(null, 'caption', content)}
                                platform={R.propOr(null, 'platform', content)}
                                socialId={R.propOr(null, 'social_id', content)}
                                contentUrl={R.propOr(null, 'content_url', content)}
                                profileImageUrl={R.propOr(null, 'profile_image_url', content)}
                                onReject={(guidelines, feedback) =>
                                    this.onReject(content.id, guidelines, feedback)
                                }
                                onDoNotDistribute={feedback => {
                                    this.onDoNotDistribute(
                                        content.id,
                                        feedback,
                                        featureFlagValue === 'on' || featureFlagValue === true
                                    );
                                }}
                                tags={R.propOr([], 'tags', content)}
                            />
                        )}
                        renderDefaultCallback={
                            <ContentReviewWorkflow
                                rejecting
                                isModal
                                guidelines={guidelines}
                                contentId={R.propOr(null, 'id', content)}
                                caption={R.propOr(null, 'caption', content)}
                                platform={R.propOr(null, 'platform', content)}
                                socialId={R.propOr(null, 'social_id', content)}
                                contentUrl={R.propOr(null, 'content_url', content)}
                                profileImageUrl={R.propOr(null, 'profile_image_url', content)}
                                onReject={(guidelines, feedback) =>
                                    this.onReject(content.id, guidelines, feedback)
                                }
                                onDoNotDistribute={feedback => {
                                    this.onDoNotDistribute(content.id, feedback, false);
                                }}
                                tags={R.propOr([], 'tags', content)}
                            />
                        }
                    />
                </div>
            </Modal>
        );
    };

    render() {
        const {contents, selectedCreatorIds, allCreators} = this.state;

        const {
            campaign_content_progress: {approved, pending, goal}
        } = this.props;
        const {filter, isLoading, sort} = this.state;

        const header = (
            <div
                className={classNames('layout-spread content-list__header', {
                    'content-list__header--no-border': !contents || !contents.length
                })}
            >
                <SubmittedContentLabels submittedContent={approved + pending} />
                <div className="layout-row layout-stack--right content-list__header__actions">
                    <div className="content-list__header__filter">
                        <CreatorFilter
                            creators={allCreators}
                            selectedCreatorIds={selectedCreatorIds}
                            onCreatorSelect={this.handleCreatorSelect}
                            onClearFilters={this.clearCreatorFilters}
                            isLoading={isLoading}
                        />
                        <OptionsDropdown
                            selected={filter}
                            options={contentFilters}
                            onChange={this.onFilter}
                        />
                        <OptionsDropdown
                            selected={sort}
                            options={contentSorts}
                            leftIcon={require('images/icons/svg/fa-sort.svg')}
                            onChange={this.onSort}
                        />
                    </div>
                    <ApprovalAndListViewToggle list onApprovalView={this.gotoApprovalView} />
                </div>
            </div>
        );

        const progress = this.props.campaign_content_progress ? (
            <div className="content-progress">
                <CampaignContentProgress
                    approved={approved || 0}
                    pending={pending || 0}
                    required={goal || 0}
                />
            </div>
        ) : null;

        if (isLoading) {
            return (
                <div className="content-list">
                    <div>
                        <div className="content-list__header">
                            <p className="content-list__header__text">Content Approval</p>
                        </div>
                        {progress}
                        {header}
                    </div>
                    <div className="content-list__loading layout-middle layout-center">
                        <LoadingSpinner additionalClasses="fa-3x" />
                    </div>
                </div>
            );
        }

        return (
            <div className="content-list">
                {this.renderModal()}
                <div>
                    <div className="content-list__header">
                        <p className="content-list__header__text">Content Approval</p>
                    </div>
                    {progress}
                    {header}
                </div>
                {this.renderList()}
            </div>
        );
    }
}

export default connect(
    R.pick([
        'campaign',
        'campaign_content_progress',
        'campaign_contents',
        'campaign_guidelines',
        'hasRejectionPermission'
    ]),
    R.pick(
        [
            'fetchCampaign',
            'rejectContent',
            'approveContent',
            'fetchCampaignContents',
            'doNotDistributeContent',
            'fetchCampaignGuidelines',
            'fetchOldestCampaignContent',
            'fetchCampaignContent',
            'fetchCampaignContentProgress',
            'clearContentReviewData'
        ],
        CampaignDucks
    )
)(ContentList);



// WEBPACK FOOTER //
// ./brand/content_approval/content_list.js